/**
 * ListingDetailPage — the page shell for one Listing (Change 20).
 *
 * Keeps data fetching, the header (title, status badge, paid-period indicator, AI agent pill),
 * the lifecycle actions and their dialogs. The body depends on status:
 *   - draft: redirected into the guided listing setup at its first incomplete step (BR-107);
 *   - active | paused | closed: the hash-addressed ListingSections view, with the "Published on
 *     Resida. Now get it seen." GoLiveNotice after a first publish (the guide hands the message over
 *     in router state).
 * The header also carries the marketing state chip and, once the listing is not active but still has
 * posts on other sites, the take-down notice; Pause and Close say what happens to those posts
 * (listing-marketing-hub BR-4, BR-15). The posts come from one shared query (useListingExternalPosts).
 * The section components live in ./listing-detail/.
 *
 * Route: /leasing/listings/:id
 * US-1–6, US-15
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { useBillingSubscription } from '../../hooks/useBilling';
import { useListingExternalPosts } from '../../hooks/useListingExternalPosts';
import { useUnitTimezone } from '../../hooks/useUnitTimezone';
import type { ExternalListingPostDto } from '../../lib/external-listing-posts.api';
import { postedPosts } from '../../lib/listing-marketing';
import { MarketingStateChip } from '../../components/leasing/MarketingStateChip';
import { PostsHeadsUp } from '../../components/leasing/PostsHeadsUp';
import { PostsImpactList } from '../../components/leasing/PostsImpactList';
import { TakeDownNotice } from '../../components/leasing/TakeDownNotice';
import { cn } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { LISTING_STATUS_BADGE_VARIANTS, LISTING_STATUS_LABELS } from '../../components/leasing/listing-status';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ListingPublishDialog } from '../../components/leasing/ListingPublishDialog';
import { RenewListingDialog } from '../../components/leasing/RenewListingDialog';
import { ListingPaidPeriodIndicator } from '../../components/leasing/ListingPaidPeriodIndicator';
import {
  fetchListing,
  fetchUnits,
  pauseListing,
  closeListing,
  reopenListing,
  fetchListingLeads,
} from '../../lib/leasing.api';
import { GoLiveNotice } from './listing-detail/GoLiveNotice';
import { clearGoLiveHandoff, peekGoLiveHandoff } from './listing-detail/goLiveHandoff';
import { LifecycleActions } from './listing-detail/LifecycleActions';
import { ListingSections } from './listing-detail/ListingSections';
import { DraftGuideRedirect } from './listing-guide/ListingGuidePage';
import { ShareListingButton } from './listing-detail/ShareCopyButtons';
import { formatDate, formatRentCents, LifecycleConfirmDialog } from './listing-detail/shared';
import { useListingAgentPolicy } from './listing-detail/useListingAgentPolicy';
import { formatUnitLabel } from '@resida/shared';

/** Shown on the Contact info editor when publishing is attempted without a contact name and phone (BR-LSI6-02). */
const CONTACT_INFO_REQUIRED_MESSAGE =
  'Add a contact name and phone before publishing. Nothing has been charged.';

// pageSize 100 × 10 pages = 1000, matching apps/api/src/services/leasing/lead.service.ts's
// OPEN_LEAD_IDS_MAX cascade cap.
const OPEN_LEADS_WARNING_MAX_PAGES = 10;

// ---------------------------------------------------------------------------
// Lifecycle confirmations (Close; Pause / Reopen) — publishing / renewing is a paid flow
// with its own dialogs (ListingPublishDialog / RenewListingDialog).
// ---------------------------------------------------------------------------

interface CloseListingDialogProps {
  open: boolean;
  openLeadCount: number;
  onConfirm: (notifyOpenLeads: boolean) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  /** The listing's external post records and the property timezone, for the posts note (BR-15b). */
  externalPosts: ExternalListingPostDto[] | undefined;
  timezone: string | undefined;
}

function CloseListingDialog({
  open,
  openLeadCount,
  onConfirm,
  onCancel,
  isSubmitting,
  externalPosts,
  timezone,
}: CloseListingDialogProps) {
  const [notifyOpenLeads, setNotifyOpenLeads] = useState(false);

  // This dialog is mounted unconditionally at its usage site (only the inner
  // Dialog's `open` prop toggles visibility), so its local state would otherwise
  // survive a cancel-and-reopen within the same page session. That's a real risk
  // for `notifyOpenLeads`: AC-4 frames it as an opt-in that defaults unchecked, and
  // a stale `true` here would silently re-send the bulk "listing no longer
  // available" email on a later, unrelated close attempt. Reset it whenever the
  // dialog transitions to closed (cancel, successful close, or failed close all
  // route through `open` going false) so each open starts fresh.
  useEffect(() => {
    if (!open) {
      setNotifyOpenLeads(false);
    }
  }, [open]);

  return (
    <LifecycleConfirmDialog
      open={open}
      onOpenChange={(next) => { if (!next) onCancel(); }}
      title="Close Listing"
      description="Closing this listing will deactivate its intake email address and remove it from active pipelines. You can reopen it later, or create a new listing to re-list the unit with a fresh intake address."
      confirmLabel="Close Listing"
      pendingLabel="Closing…"
      onConfirm={() => onConfirm(notifyOpenLeads)}
      isSubmitting={isSubmitting}
      destructive
      testIdPrefix="close-listing"
    >
      {openLeadCount > 0 && (
        <>
          <p className="text-sm text-warning mb-3" data-testid="close-listing-open-lead-warning" role="alert">
            Closing this listing will also close its {openLeadCount} open lead(s).
          </p>
          <label className="flex items-center gap-2 text-sm cursor-pointer mb-4 min-h-[44px]">
            <input
              type="checkbox"
              id="close-listing-notify"
              data-testid="close-listing-notify-checkbox"
              checked={notifyOpenLeads}
              onChange={(e) => setNotifyOpenLeads(e.target.checked)}
              className="rounded border-input"
            />
            Send &quot;listing no longer available&quot; email to the open lead(s)
          </label>
        </>
      )}
      <div className="mb-4">
        <PostsImpactList action="close" posts={externalPosts} timezone={timezone} />
      </div>
    </LifecycleConfirmDialog>
  );
}

type PendingLifecycleAction = 'pause' | 'reopen';

function getLifecycleConfirmCopy(
  action: PendingLifecycleAction
): { title: string; description: string; confirmLabel: string; pendingLabel: string } {
  switch (action) {
    case 'pause':
      // BR-15b: the four effects are listed in the dialog body (PauseEffects + PostsImpactList).
      return {
        title: 'Pause this listing?',
        description: 'Here is what happens to your listing and its marketing.',
        confirmLabel: 'Pause listing',
        pendingLabel: 'Pausing…',
      };
    case 'reopen':
      return {
        title: 'Reopen Listing',
        description:
          'Reopening returns this listing to Draft status and reactivates its intake email address behind the scenes, but the listing stays hidden from prospects until you activate it again.',
        confirmLabel: 'Reopen',
        pendingLabel: 'Reopening…',
      };
  }
}

/** BR-15b: what pausing does to the Resida page, the Resida email and the pipeline. */
function PauseEffects() {
  return (
    <ul className="mb-3 flex flex-col gap-2 text-sm text-foreground" data-testid="pause-effects">
      <li>
        <span className="font-semibold">Your Resida page</span> stays up with a &quot;paused&quot; notice. Apply and
        showing buttons are hidden.
      </li>
      <li>
        <span className="font-semibold">Your Resida email</span> stays active. Anyone who writes to it gets an
        automatic &quot;on hold&quot; reply.
      </li>
      <li>
        <span className="font-semibold">Your pipeline</span> is unaffected. Keep reviewing, scheduling and deciding as
        normal.
      </li>
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // The first-publish hand-off from the guide: read purely at first render, removed in an effect so it shows once.
  const [handedOverMessage] = useState<string | null>(() => (id ? peekGoLiveHandoff(queryClient, id) : null));
  useEffect(() => {
    if (id) clearGoLiveHandoff(queryClient, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, []);

  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingLifecycleAction | null>(null);
  const [feeDialog, setFeeDialog] = useState<'publish' | 'renew' | null>(null);
  // A first publish through the guide's Review step hands its message over in the query cache: show the go-live notice.
  const [feeSuccessMessage, setFeeSuccessMessage] = useState<string | null>(handedOverMessage);
  // Set when a first publish completed: the success message renders as the go-live notice.
  const [goLive, setGoLive] = useState(handedOverMessage != null);
  const [isActing, setIsActing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [contactInfoError, setContactInfoError] = useState<string | null>(null);

  // The plan's listing fee / period, for the Republish and Renew labels and the publish review.
  const { data: subscription } = useBillingSubscription();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => fetchListing(id!),
    enabled: !!id,
  });
  // One query for the chip, the heads-up, the confirmations, the take-down notice and the Marketing tab.
  const { data: externalPosts } = useListingExternalPosts(id);
  const { data: timezone } = useUnitTimezone(data?.listing?.unitId);

  const listing = data?.listing;
  const screeningCriteria = data?.screeningCriteria ?? null;
  const intakeAddress = data?.intakeAddress ?? null;
  const contactPhone = data?.contactPhone ?? null;
  const contactName = data?.contactName ?? null;
  const canDecide = data?.permissions?.canDecide ?? false;
  const canManage = data?.permissions?.canManage ?? false;

  // Server-computed (DC-51): a current paid period on the unit OR the landlord's agent add-on is
  // effectively active. Gates the agent pill and the Contact & agent section on a published
  // listing — entitlement is still enforced server-side where the agent acts (BR-101).
  const leasingAgentAvailable = listing?.leasingAgentAvailable ?? false;

  // Per-listing resolved enablement — drives the header pill. Same key as the editor and the
  // guide state; GET needs listing#manage.
  const { data: agentPolicyData } = useListingAgentPolicy(id ?? '', !!listing && leasingAgentAvailable && canManage);

  // AC-L6: the H1 shows only "$rent/mo" today, giving no identity to distinguish this
  // listing from another one open in an adjacent tab. Same unit-lookup pattern as
  // ListingsIndexPage's unitsMap — the listing detail response only carries `unitId`.
  const { data: unitsData } = useQuery({
    queryKey: ['units'],
    queryFn: () => fetchUnits(),
  });
  const unit = listing ? (unitsData?.units ?? []).find((u) => u.id === listing.unitId) : undefined;
  const unitLabel = unit
    ? [formatUnitLabel(unit.unitNumber), unit.propertyName].filter(Boolean).join(' · ')
    : null;
  const unitName = unit ? (formatUnitLabel(unit.unitNumber) ?? unit.propertyName) : 'this unit';
  const feeDialogUnitLabel = unitLabel;

  // Distinct queryKey from PipelineBoard's `['listing', id, 'leads']` (pageSize 100, single
  // page — fine for that board's own display purposes): the close-listing warning (AC-3) needs
  // an ACCURATE count, so it paginates through every page up to OPEN_LEADS_WARNING_MAX_PAGES,
  // matching lead.service.ts's OPEN_LEAD_IDS_MAX cascade cap — a listing with more open leads
  // than the cascade itself will ever process should not surface a smaller number here than
  // what confirming the dialog will actually affect. No new backend endpoint added. Kept in the
  // shell: the Close dialog needs it on every section.
  const { data: leadsForCloseWarning } = useQuery({
    queryKey: ['listing', id, 'open-lead-count'],
    queryFn: async () => {
      const first = await fetchListingLeads(id!, { pageSize: 100 });
      const items = [...first.items];
      let page = 2;
      while (items.length < first.totalCount && page <= OPEN_LEADS_WARNING_MAX_PAGES) {
        const next = await fetchListingLeads(id!, { pageSize: 100, page });
        items.push(...next.items);
        page += 1;
      }
      return items;
    },
    enabled: !!id,
  });
  const openLeadCount = (leadsForCloseWarning ?? []).filter(
    (lead) => lead.stage !== 'closed'
  ).length;

  const invalidateListing = () => {
    void queryClient.invalidateQueries({ queryKey: ['listing', id] });
    void queryClient.invalidateQueries({ queryKey: ['listings'] });
  };

  // A paid publish/renew (or free republish) succeeded: the dialog has built the message.
  const handleFeeCompleted = (message: string) => {
    setFeeDialog(null);
    setActionError(null);
    setContactInfoError(null);
    setFeeSuccessMessage(message);
    setGoLive(false);
    invalidateListing();
  };

  const dismissFeeSuccess = () => {
    setFeeSuccessMessage(null);
    setGoLive(false);
  };

  // The publish was refused before any charge for want of a contact name/phone (the dialog
  // explained it, or the guard below caught it): close it and switch to Contact & agent.
  const handleContactInfoRequired = () => {
    setFeeDialog(null);
    setContactInfoError(CONTACT_INFO_REQUIRED_MESSAGE);
    window.location.hash = 'contact-agent';
  };

  // Paused re-activate / Republish: contact guard first, then the fee dialog.
  const handleActivate = () => {
    if (!(contactPhone?.trim() && contactName?.trim())) {
      handleContactInfoRequired();
      return;
    }
    setFeeDialog('publish');
  };

  const handlePause = async () => {
    if (!id) return;
    setActionError(null);
    setIsActing(true);
    try {
      await pauseListing(id);
      invalidateListing();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to pause listing');
    } finally {
      setIsActing(false);
      setPendingAction(null);
    }
  };

  const handleClose = async (notifyOpenLeads: boolean) => {
    if (!id) return;
    setActionError(null);
    setIsActing(true);
    try {
      await closeListing(id, { notifyOpenLeads });
      setShowCloseDialog(false);
      invalidateListing();
      // Reflect the lead-closure cascade on the pipeline board and the close-warning count.
      void queryClient.invalidateQueries({ queryKey: ['listing', id, 'leads'] });
      void queryClient.invalidateQueries({ queryKey: ['listing', id, 'open-lead-count'] });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to close listing');
      setShowCloseDialog(false);
    } finally {
      setIsActing(false);
    }
  };

  const handleReopen = async () => {
    if (!id) return;
    setActionError(null);
    setIsActing(true);
    try {
      await reopenListing(id);
      invalidateListing();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reopen listing');
    } finally {
      setIsActing(false);
      setPendingAction(null);
    }
  };

  if (isLoading) {
    return (
      <div
        data-testid="listing-detail-page"
        className="w-full max-w-[1600px] mx-auto pb-6 px-[var(--page-padding-x)]"
      >
        <div className="h-8 mb-4">
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div
        data-testid="listing-detail-page"
        className="w-full max-w-[1600px] mx-auto pb-6 px-[var(--page-padding-x)]"
      >
        <div className="py-12 text-center">
          <p
            data-testid="listing-detail-error"
            className="text-destructive mb-4"
          >
            {error instanceof Error ? error.message : 'Listing not found'}
          </p>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // A draft is revised in the guided setup, resuming at its first incomplete step.
  if (listing.status === 'draft') return <DraftGuideRedirect listingId={listing.id} />;

  const agentOn = agentPolicyData?.resolvedEnablement === 'enabled';
  // BR-2: derived from the live post records once loaded (they follow every record / remove), else
  // from the listing's own summary.
  const postedCount = externalPosts ? postedPosts(externalPosts).length : (listing.marketing?.postedCount ?? 0);

  return (
    <div
      data-testid="listing-detail-page"
      className="w-full max-w-[1600px] mx-auto pb-6 px-[var(--page-padding-x)]"
    >
      {/* Back link */}
      <Link
        to="/leasing/listings"
        data-testid="listing-detail-back"
        className="inline-flex items-center gap-1 min-h-[44px] text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        All Listings
      </Link>

      {/* Page header */}
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground break-words" data-testid="listing-detail-h1">
            {unitLabel ? `${unitLabel} — ` : ''}
            {formatRentCents(listing.askingRentCents)}/mo
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Available {formatDate(listing.availableDate)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            data-testid="listing-status-badge"
            variant={LISTING_STATUS_BADGE_VARIANTS[listing.status]}
          >
            {LISTING_STATUS_LABELS[listing.status]}
          </Badge>
          <MarketingStateChip listingId={listing.id} status={listing.status} postedCount={postedCount} variant="header" />
          <ListingPaidPeriodIndicator
            listing={listing}
            variant="header"
            periodDays={subscription?.listingPeriodDays ?? null}
            onRenew={() => setFeeDialog('renew')}
          />
          {leasingAgentAvailable && (
            <a
              // The Contact & agent section (DetailTabs follows the hash).
              href="#contact-agent"
              data-testid="listing-agent-status-pill"
              className="inline-flex items-center min-h-[44px]"
            >
              <Badge
                variant={agentOn ? 'info' : 'secondary'}
                className="gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium"
              >
                <span
                  aria-hidden="true"
                  className={cn('h-1.5 w-1.5 rounded-full', agentOn ? 'bg-info' : 'bg-muted-foreground')}
                />
                {agentPolicyData
                  ? `AI Agent: ${agentOn ? 'On' : 'Off'}${
                      agentOn ? ` · Review replies: ${agentPolicyData.resolvedReview === 'required' ? 'Yes' : 'No'}` : ''
                    }`
                  : 'AI Agent'}
              </Badge>
            </a>
          )}
        </div>
      </div>

      {/* Lifecycle actions */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <LifecycleActions
            listing={listing}
            canDecide={canDecide}
            onActivate={handleActivate}
            onPause={() => setPendingAction('pause')}
            onClose={() => setShowCloseDialog(true)}
            onReopen={() => setPendingAction('reopen')}
            isActing={isActing}
            republishFeeCents={subscription?.listingFeeCents ?? null}
          />
          <ShareListingButton listing={listing} intakeAddress={intakeAddress} />
        </div>
        {/* BR-15a: below the whole button row, so it never splits the buttons. */}
        {listing.status === 'active' && (
          <div className="mt-2 empty:hidden">
            <PostsHeadsUp posts={externalPosts} />
          </div>
        )}
        {feeSuccessMessage && !goLive && (
          <div
            data-testid="listing-fee-success"
            role="status"
            className="mt-2 flex items-start justify-between gap-3 p-3 bg-success/10 border border-success/25 rounded-md text-success text-sm"
          >
            <span>{feeSuccessMessage}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-[44px] -my-2"
              data-testid="listing-fee-success-dismiss"
              onClick={dismissFeeSuccess}
            >
              Dismiss
            </Button>
          </div>
        )}
        <div className="mt-2 empty:hidden">
          <TakeDownNotice listingId={listing.id} status={listing.status} posts={externalPosts} onListingPage />
        </div>
        {actionError && (
          <div
            data-testid="listing-action-error"
            className="mt-2 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
            role="alert"
          >
            {actionError}
          </div>
        )}
      </div>

      {feeSuccessMessage && goLive && (
        <GoLiveNotice
          message={feeSuccessMessage}
          intakeAddress={intakeAddress}
          publicUrl={listing.publicUrl ?? null}
          onDismiss={dismissFeeSuccess}
        />
      )}

      <ListingSections
          listing={listing}
          screeningCriteria={screeningCriteria}
          intakeAddress={intakeAddress}
          contactName={contactName}
          contactPhone={contactPhone}
          contactInfoError={contactInfoError}
          onReactivate={handleActivate}
          canManage={canManage}
          leasingAgentAvailable={leasingAgentAvailable}
          onSaved={() => {
            invalidateListing();
            setContactInfoError(null);
          }}
        />

      {/* Close dialog */}
      <CloseListingDialog
        open={showCloseDialog}
        openLeadCount={openLeadCount}
        onConfirm={(notifyOpenLeads) => void handleClose(notifyOpenLeads)}
        onCancel={() => setShowCloseDialog(false)}
        isSubmitting={isActing}
        externalPosts={externalPosts}
        timezone={timezone}
      />

      {/* Pause/Reopen confirmation */}
      <LifecycleConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(next) => { if (!next) setPendingAction(null); }}
        {...getLifecycleConfirmCopy(pendingAction ?? 'pause')}
        onConfirm={() => {
          if (pendingAction === 'pause') void handlePause();
          else if (pendingAction === 'reopen') void handleReopen();
        }}
        isSubmitting={isActing}
        testIdPrefix="lifecycle-confirm"
      >
        {pendingAction === 'pause' && (
          <div className="mb-4">
            <PauseEffects />
            <PostsImpactList action="pause" posts={externalPosts} timezone={timezone} />
          </div>
        )}
      </LifecycleConfirmDialog>

      {/* Publish / Republish (paid, with consent) and Renew */}
      <ListingPublishDialog
        open={feeDialog === 'publish'}
        onOpenChange={(next) => { if (!next) setFeeDialog(null); }}
        listing={listing}
        unitName={unitName}
        unitLabel={feeDialogUnitLabel}
        onCompleted={handleFeeCompleted}
        onContactInfoRequired={handleContactInfoRequired}
        onPaidNotPublished={invalidateListing}
      />
      <RenewListingDialog
        open={feeDialog === 'renew'}
        onOpenChange={(next) => { if (!next) setFeeDialog(null); }}
        listing={listing}
        unitName={unitName}
        unitLabel={feeDialogUnitLabel}
        onCompleted={handleFeeCompleted}
      />
    </div>
  );
}
